import type { TypeId } from '@unipile/db';
import type { ProviderAuthenticationIntentResponse, CaptchaCheckpoint } from '@unipile/types';
import { Loader2Icon } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useSolveCheckpoint } from '../../../features/accounts/api/solve-checkpoint';
import { useProvider } from '../../../hooks/useProvider';
import { ArkoseLabsCaptcha } from '../captcha/ArkoseLabsCaptcha';
import { WizardStepContainer } from '../containers/WizardStepContainer';

/**
 * A checkpoint that requires a captcha to be solved.
 */
export function CaptchaCheckpoint({
  checkpoint,
  onSuccess,
  onError,
  intent_id,
}: {
  onSuccess: (response: ProviderAuthenticationIntentResponse) => void;
  onError: (error: unknown) => void;
  checkpoint: CaptchaCheckpoint;
  intent_id: TypeId<'account'>;
}) {
  const { t } = useTranslation();
  const { provider } = useProvider();

  const { mutate, isPending } = useSolveCheckpoint({
    mutationConfig: {
      onSuccess,
      onError,
    },
  });

  const handleSuccess = (code: string) => {
    mutate({ data: { code, intent_id } });
  };

  return (
    <WizardStepContainer title={t('CAPTCHA.title', { ns: provider })} description={t('CAPTCHA.description', { ns: provider })}>
      <div className="flex items-center justify-center">
        {isPending ? (
          <Loader2Icon className="h-4 w-4 animate-spin" />
        ) : (
          <ArkoseLabsCaptcha
            onError={console.error}
            onSuccess={handleSuccess}
            data={checkpoint.data!}
            public_key={checkpoint.public_key!}
          />
        )}
      </div>
    </WizardStepContainer>
  );
}
